import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Article } from "@/hooks/useArticles";

// Import local fallback images
import followDream from "@/assets/images/follow-dream.webp";
import roadtripVan from "@/assets/images/roadtrip-van.webp";
import airplaneSunset from "@/assets/images/airplane-sunset.jpg";
import italyCoast from "@/assets/images/italy-coast.webp";
import airportSilhouette from "@/assets/images/airport-silhouette.webp";
import lakeBoat from "@/assets/images/lake-boat.webp";
import friendsMountain from "@/assets/images/friends-mountain.webp";

const placeholderImages = [
  followDream, roadtripVan, airplaneSunset, italyCoast,
  airportSilhouette, lakeBoat, friendsMountain,
];

function getImageIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash) % placeholderImages.length;
}

interface BlogsSectionProps {
  articles: Article[];
  categoryName: string;
  categorySlug: string;
}

export default function BlogsSection({ articles, categoryName, categorySlug }: BlogsSectionProps) {
  if (articles.length === 0) return null;

  return (
    <section className="mt-16 pt-10 border-t border-border">
      <div className="flex flex-col items-center text-center mb-10">
        <span className="inline-block rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground mb-4 tracking-wider uppercase">
          Artikler
        </span>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
          Flere artikler fra{" "}
          <Link to={`/kategori/${categorySlug}`} className="text-primary hover:underline">
            {categoryName}
          </Link>
        </h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => {
          const imageUrl = article.featured_image || placeholderImages[getImageIndex(article.id)];
          const formattedDate = new Date(article.created_at).toLocaleDateString("da-DK", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });

          return (
            <Link
              key={article.id}
              to={`/artikel/${article.slug}`}
              className="group block rounded-2xl border border-border bg-card overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-primary/30"
            >
              <div className="aspect-[16/10] overflow-hidden relative">
                <img
                  src={imageUrl}
                  alt={article.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 rounded-full bg-primary/90 px-3 py-1 text-[11px] font-semibold text-primary-foreground uppercase tracking-wide">
                  #{categoryName}
                </span>
              </div>

              <div className="p-5 flex flex-col gap-2">
                <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2.5 transition-all">
                    <ArrowRight className="w-4 h-4" />
                    Læs mere
                  </span>
                  <span className="text-xs text-muted-foreground">{formattedDate}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
